import { gql } from '@apollo/client';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import {
  Avatar,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Text,
} from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useCallback, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { CollapsibleText } from 'src/components/common/collapsibleText/CollapsibleText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { useIdentity } from 'src/hooks/useIdentity';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CommentStatus } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { proposalHomepageRouteConfig, proposalRouteConfig } from '../../routes';
import { CommentMarkdownEditor } from '../CommentMarkdownEditor/CommentMarkdownEditor';
import { SafeCommentMarkdown } from '../CommentMarkdownEditor/SafeCommentMarkdown';
import { useGetUsersForMentions } from '../CommentMarkdownEditor/useMentionUsers';

import { useEditProposalComment } from './useEditProposalComment';

export const SingleProposalCommentFragment = gql`
  fragment SingleProposalCommentFragment on ProposalComment {
    id
    message
    status
    createdAt
    updatedAt
    createdBy {
      id
      ... on User {
        fullName
        avatarUrl
      }
    }
  }
`;

export const SingleProposalComment = ({
  comment,
  commentThreadId,
  proposalId,
  loading,
  className,
  shouldShowActions = true,
  shouldClampText,
  shouldShowAuthor = true,
  shouldShowTimestamp = true,
  shouldHighlight,
  usersForMentions,
}: {
  comment: GraphQLTypes.SingleProposalCommentFragment;
  commentThreadId?: string;
  proposalId: string;
  loading?: boolean;
  className?: string;
  shouldShowActions?: boolean;
  shouldClampText?: boolean;
  shouldShowAuthor?: boolean;
  shouldShowTimestamp?: boolean;
  shouldHighlight?: boolean;
  usersForMentions: ReturnType<typeof useGetUsersForMentions>;
}) => {
  const [shouldShowEditMode, setShouldShowEditMode] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { me } = useIdentity();

  const location = useLocation();
  const history = useHistory();
  const { graphId, graphVariant } = useRouteParams(proposalRouteConfig);

  const { editComment } = useEditProposalComment({
    proposalId,
    commentId: comment.id,
  });

  const handleEditComment = useCallback(
    async (getValue: () => string) => {
      const value = getValue();

      if (value.trim().length === 0) {
        // open up modal
        history.push(
          setOverlayVisibleLocation({
            location,
            additionalSearchParameters: {
              proposalCommentId: comment.id,
              commentThreadId,
            },
            overlayName: Config.modals.deleteProposalComment,
          }),
        );
        setShouldShowEditMode(false);
        setErrorMessage(undefined);
        return;
      }
      setIsSubmitting(true);
      const { data: responseData } = await editComment(value);
      setIsSubmitting(false);
      if (
        responseData?.proposal.__typename === 'ProposalMutation' &&
        (responseData.proposal.editComment.__typename ===
          'ParentChangeProposalComment' ||
          responseData.proposal.editComment.__typename ===
            'ReplyChangeProposalComment' ||
          responseData.proposal.editComment.__typename ===
            'ParentGeneralProposalComment' ||
          responseData.proposal.editComment.__typename ===
            'ReplyGeneralProposalComment')
      ) {
        setShouldShowEditMode(false);
        setErrorMessage(undefined);
      } else {
        setErrorMessage('An error has occurred. Please try again.');
      }
    },
    [comment.id, commentThreadId, editComment, history, location],
  );

  const commentIsDeleted =
    'status' in comment && comment.status === CommentStatus.DELETED;
  const isCommentAuthor = me?.id === comment.createdBy?.id;
  const shouldShowActionsMenu =
    !shouldShowEditMode && shouldShowActions && !commentIsDeleted;

  const copyCommentLink = useCallback(() => {
    const { pathname = '', search = '' } = proposalHomepageRouteConfig.location(
      {
        graphId,
        graphVariant,
        focusCommentId: comment.id,
        focusThreadId: commentThreadId,
      },
    );
    copy(`${window.location.origin}${pathname}?${search}`);
  }, [comment.id, commentThreadId, graphId, graphVariant]);

  return (
    <div
      id={`comment-${comment.id}`}
      className={classnames('flex space-x-3', className)}
    >
      {shouldShowAuthor && (
        <Avatar
          size="sm"
          {...(comment.createdBy?.__typename === 'User'
            ? {
                name: comment.createdBy.fullName,
                src: comment.createdBy.avatarUrl ?? undefined,
              }
            : {})}
        />
      )}
      <div className="w-0 flex-1">
        {shouldShowAuthor && (
          <div className="flex items-center space-x-2 text-base">
            <Tooltip
              label="This user is no longer in the organization"
              placement="top"
              disabled={comment.createdBy?.__typename === 'User'}
            >
              <Text as="span" variant="bodySb">
                {comment.createdBy?.__typename === 'User'
                  ? comment.createdBy.fullName
                  : 'Unknown'}
              </Text>
            </Tooltip>{' '}
            {shouldShowTimestamp &&
              'createdAt' in comment &&
              !commentIsDeleted && (
                <div className="text-secondary">
                  <RelativeTimestamp
                    value={comment.createdAt}
                    withTooltip={false}
                  />{' '}
                  {'updatedAt' in comment && !!comment.updatedAt && '(edited)'}
                </div>
              )}
            {shouldShowActionsMenu && (
              <Menu isLazy placement="bottom-start">
                <MenuButton
                  as={IconButton}
                  size="sm"
                  variant="hidden"
                  className={classnames(loading && 'invisible')}
                  icon={<IconOverflow />}
                />
                <MenuContent>
                  <MenuList>
                    {isCommentAuthor && (
                      <>
                        <MenuItem onClick={() => setShouldShowEditMode(true)}>
                          Edit Comment
                        </MenuItem>
                        <MenuItem
                          className="text-error hover:bg-error hover:text-error"
                          as={Link}
                          to={setOverlayVisibleLocation({
                            location,
                            additionalSearchParameters: {
                              proposalCommentId: comment.id,
                              commentThreadId,
                            },
                            overlayName: Config.modals.deleteProposalComment,
                          })}
                        >
                          Delete Comment
                        </MenuItem>
                      </>
                    )}
                    <MenuItem onClick={copyCommentLink}>
                      Copy link to comment
                    </MenuItem>
                  </MenuList>
                </MenuContent>
              </Menu>
            )}
          </div>
        )}
        {shouldShowEditMode ? (
          <>
            {errorMessage && <ErrorMessage className="mt-2" />}
            <CommentMarkdownEditor
              usersForMentions={usersForMentions}
              defaultValue={comment.message}
              className="mt-2"
              shouldShowPlaceholderText={false}
              shouldAutoFocusEditor
              actions={({ hasChanged, getValue }) => {
                return (
                  <ButtonGroup>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setShouldShowEditMode(false);
                        setErrorMessage(undefined);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      isDisabled={!hasChanged}
                      size="sm"
                      isLoading={isSubmitting}
                      loadingText="Saving"
                      onClick={() => handleEditComment(getValue)}
                    >
                      Save
                    </Button>
                  </ButtonGroup>
                );
              }}
            />
          </>
        ) : (
          <WrapMaybe
            on={!!shouldClampText}
            element={
              <CollapsibleText
                maxLines={4}
                expandTextElement={({ onClick, isCollapsed }) =>
                  isCollapsed && (
                    <div className="mt-2 flex w-full justify-end">
                      <Button variant="buttonLink" onClick={onClick}>
                        Show full comment
                      </Button>
                    </div>
                  )
                }
              />
            }
          >
            <SafeCommentMarkdown
              comment={
                commentIsDeleted
                  ? '_This comment has been deleted._'
                  : comment.message
              }
              className={classnames(
                loading && 'opacity-70',
                shouldHighlight && 'bg-searchhighlight !text-inverted',
              )}
              usersForMentions={usersForMentions}
            />
          </WrapMaybe>
        )}
      </div>
    </div>
  );
};
